"use client";

import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "ghost" | "outline";
  loading?: boolean;
};

export function Button({
  variant = "primary",
  loading,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const styles = {
    primary:
      "bg-brand text-brand-fg shadow-[var(--shadow-sm)] hover:brightness-110 active:translate-y-px",
    outline: "border border-border bg-surface hover:bg-surface-muted",
    ghost: "text-muted hover:bg-surface-muted hover:text-foreground",
  }[variant];

  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-medium transition duration-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50",
        styles,
        className,
      )}
    >
      {loading && <Loader2 className="size-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-muted">{hint}</span>}
    </label>
  );
}

const control =
  "w-full rounded-xl border border-border bg-input px-3.5 py-2.5 text-sm outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/25";

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={cn(control, props.className)} />;
}

export function Textarea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={cn(control, "resize-y", props.className)} />;
}

export function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={cn(control, props.className)} />;
}

export function Card({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border bg-surface p-5 shadow-[var(--shadow-sm)]",
        className,
      )}
    >
      {children}
    </div>
  );
}

export function ErrorNote({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <p className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-600 dark:text-rose-400">
      {children}
    </p>
  );
}

/** Uzun işlemlerde ilerleme; toplam bilinmiyorsa belirsiz mod. */
export function Progress({ value, label }: { value?: number; label?: string }) {
  return (
    <div>
      {label && <p className="mb-2 text-sm text-muted">{label}</p>}
      <div className="h-2 overflow-hidden rounded-full bg-surface-muted">
        <div
          className={cn(
            "h-full rounded-full bg-brand transition-[width] duration-300",
            value === undefined && "w-1/3 animate-pulse",
          )}
          style={value !== undefined ? { width: `${Math.round(value * 100)}%` } : undefined}
        />
      </div>
    </div>
  );
}

/** Hesaplama sonuçları için tek tip vurgu/normal kutu. */
export function Stat({
  etiket,
  deger,
  alt,
  vurgu,
}: {
  etiket: string;
  deger: React.ReactNode;
  alt?: string;
  vurgu?: boolean;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border p-4",
        vurgu ? "border-brand/40 bg-brand-soft" : "border-border bg-surface",
      )}
    >
      <p className="text-xs text-muted">{etiket}</p>
      <p className={cn("mt-1 text-2xl font-semibold tabular-nums", vurgu && "text-brand")}>{deger}</p>
      {alt && <p className="mt-1 text-xs text-muted">{alt}</p>}
    </div>
  );
}

/** "Kopyala" düğmesi — panoya kopyalar, 1.5 sn "Kopyalandı" gösterir. */
export function CopyButton({ text, className }: { text: string; className?: string }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        navigator.clipboard?.writeText(text);
        const el = e.currentTarget;
        const ilk = el.textContent;
        el.textContent = "Kopyalandı ✓";
        setTimeout(() => {
          el.textContent = ilk;
        }, 1500);
      }}
      className={cn(
        "rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium transition hover:bg-surface-muted",
        className,
      )}
    >
      Kopyala
    </button>
  );
}
